import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, IndianRupee, Loader2, PackageCheck, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { api, apiError, fmt } from "@/lib/api";

const EMPTY_ITEM = { product_id: "", sku: "", qty: 1, unit_cost: "", gst_rate: 18 };
const PAY_METHODS = [
  { id: "bank_transfer", label: "Bank transfer" },
  { id: "upi", label: "UPI" },
  { id: "cash", label: "Cash" },
  { id: "cheque", label: "Cheque" },
  { id: "other", label: "Other" },
];

export default function AdminPurchases() {
  const [purchases, setPurchases] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [receiveNow, setReceiveNow] = useState(false);
  const [paying, setPaying] = useState(null);       // purchase being paid
  const [payForm, setPayForm] = useState({ amount: "", payment_date: "", method: "bank_transfer", reference: "", note: "" });
  const [payBusy, setPayBusy] = useState(false);
  const [payHistory, setPayHistory] = useState({}); // purchase_id → payments[]

  const load = useCallback(() => {
    Promise.all([api.get("/admin/purchases"), api.get("/admin/suppliers"), api.get("/admin/products")])
      .then(([p, s, pr]) => {
        setPurchases(p.data.purchases);
        setSuppliers(s.data.suppliers.filter((x) => x.active));
        setProducts(pr.data.filter((x) => x.active && !x.is_deleted));
      })
      .catch((e) => toast.error(apiError(e, "Couldn't load purchases")));
  }, []);
  useEffect(load, [load]);

  const setItem = (i, k, v) => setItems((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const variantsOf = (pid) => products.find((p) => p.id === pid)?.variants?.filter((v) => v.enabled) || [];

  const subtotal = items.reduce((n, i) => n + (parseInt(i.qty) || 0) * (parseFloat(i.unit_cost) || 0), 0);
  const tax = items.reduce((n, i) => n + (parseInt(i.qty) || 0) * (parseFloat(i.unit_cost) || 0) * (parseFloat(i.gst_rate) || 0) / 100, 0);

  const submit = async () => {
    if (!supplierId) { toast.error("Select a supplier"); return; }
    const clean = items.filter((i) => i.product_id && i.sku && parseInt(i.qty) > 0 && i.unit_cost !== "");
    if (!clean.length) { toast.error("Add at least one complete item row"); return; }
    setBusy(true);
    try {
      await api.post("/admin/purchases", {
        supplier_id: supplierId, reference_invoice: reference, notes, receive_now: receiveNow,
        items: clean.map((i) => ({ product_id: i.product_id, sku: i.sku, qty: parseInt(i.qty),
                                   unit_cost: parseFloat(i.unit_cost), gst_rate: parseFloat(i.gst_rate) || 0 })),
      });
      toast.success(receiveNow ? "Purchase received into inventory" : "Purchase order created");
      setCreating(false);
      setSupplierId(""); setItems([{ ...EMPTY_ITEM }]); setReference(""); setNotes("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't create the purchase"));
    } finally {
      setBusy(false);
    }
  };

  const receive = async (p) => {
    try {
      await api.post(`/admin/purchases/${p.id}/receive`);
      toast.success(`${p.purchase_number} received into inventory`);
      load();
    } catch (e) { toast.error(apiError(e, "Couldn't receive the purchase")); }
  };

  const openPay = (p) => {
    setPaying(p);
    setPayForm({ amount: String(p.outstanding), payment_date: new Date().toISOString().slice(0, 10),
                 method: "bank_transfer", reference: "", note: "" });
    if (!payHistory[p.id]) {
      api.get(`/admin/purchases/${p.id}/payments`)
        .then(({ data }) => setPayHistory((h) => ({ ...h, [p.id]: data.payments })))
        .catch(() => {});
    }
  };

  const submitPayment = async () => {
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setPayBusy(true);
    try {
      const { data } = await api.post(`/admin/purchases/${paying.id}/payments`, {
        amount, payment_date: payForm.payment_date, method: payForm.method,
        reference: payForm.reference, note: payForm.note,
      });
      toast.success(data.outstanding > 0
        ? `Payment recorded — ${fmt(data.outstanding)} still outstanding`
        : `Fully paid — ${paying.purchase_number} is settled`);
      setPayHistory((h) => ({ ...h, [paying.id]: [...(h[paying.id] || []), data.payment] }));
      setPaying(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't record the payment"));
    } finally {
      setPayBusy(false);
    }
  };

  const input = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

  return (
    <div data-testid="admin-purchases" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><ShoppingBag className="h-6 w-6 text-amber-400" /> Purchases</h1>
          <p className="mt-1 text-sm text-neutral-500">Purchase orders from suppliers — receiving adds stock to inventory.</p>
        </div>
        <button onClick={() => setCreating((v) => !v)} data-testid="purchase-create-btn"
          className="flex h-11 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-bold text-black transition-colors hover:bg-amber-400">
          <Plus className="h-4 w-4" /> New purchase
        </button>
      </div>

      {creating && (
        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-5" data-testid="purchase-form">
          <div className="grid gap-3 sm:grid-cols-3">
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="purchase-supplier" className={input}>
              <option value="">Select supplier…</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Supplier invoice ref" data-testid="purchase-ref" className={input} />
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" data-testid="purchase-notes" className={input} />
          </div>

          <div className="mt-4 space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_80px_110px_80px_36px]" data-testid={`purchase-item-${i}`}>
                <select value={it.product_id} onChange={(e) => setItem(i, "product_id", e.target.value) || setItem(i, "sku", "")}
                  data-testid={`purchase-product-${i}`} className={input}>
                  <option value="">Product…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select value={it.sku} onChange={(e) => setItem(i, "sku", e.target.value)} data-testid={`purchase-variant-${i}`} className={input}>
                  <option value="">Variant…</option>
                  {variantsOf(it.product_id).map((v) => <option key={v.sku} value={v.sku}>{v.label} ({v.sku})</option>)}
                </select>
                <input type="number" min="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} placeholder="Qty" data-testid={`purchase-qty-${i}`} className={input} />
                <input type="number" min="0" step="0.01" value={it.unit_cost} onChange={(e) => setItem(i, "unit_cost", e.target.value)} placeholder="Cost/pc ₹" data-testid={`purchase-cost-${i}`} className={input} />
                <input type="number" min="0" max="28" value={it.gst_rate} onChange={(e) => setItem(i, "gst_rate", e.target.value)} placeholder="GST%" data-testid={`purchase-gst-${i}`} className={input} />
                <button onClick={() => setItems((rows) => rows.filter((_, idx) => idx !== i))} aria-label="Remove row"
                  data-testid={`purchase-remove-${i}`} className="flex h-10 items-center justify-center rounded-lg border border-white/10 text-neutral-500 hover:text-red-300">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <button onClick={() => setItems((rows) => [...rows, { ...EMPTY_ITEM }])} data-testid="purchase-add-row"
              className="flex h-9 items-center gap-1.5 rounded-full border border-white/15 px-4 text-xs font-semibold text-neutral-300 hover:text-white">
              <Plus className="h-3.5 w-3.5" /> Add item
            </button>
            <div className="flex items-center gap-4 text-xs text-neutral-400">
              <span>Subtotal <strong className="text-white">{fmt(subtotal)}</strong> · GST <strong className="text-white">{fmt(tax)}</strong> · Total <strong className="text-amber-300">{fmt(subtotal + tax)}</strong></span>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={receiveNow} onChange={(e) => setReceiveNow(e.target.checked)} data-testid="purchase-receive-now" className="accent-amber-500" />
                Receive into stock now
              </label>
              <button onClick={submit} disabled={busy} data-testid="purchase-submit"
                className="flex h-10 items-center gap-2 rounded-full bg-amber-500 px-5 text-xs font-bold text-black hover:bg-amber-400 disabled:opacity-40">
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save purchase
              </button>
              <button onClick={() => setCreating(false)} data-testid="purchase-cancel"
                className="h-10 rounded-full border border-white/15 px-4 text-xs text-neutral-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}

      {!purchases ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : purchases.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500" data-testid="purchases-empty">
          No purchases yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/[0.07]">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid="purchases-table">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="w-8 px-3 py-3"></th><th className="px-4 py-3">PO #</th><th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Paid</th>
                <th className="px-4 py-3">Outstanding</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <Fragment key={p.id}>
                  <tr className="border-b border-white/[0.05] hover:bg-white/[0.02]" data-testid={`purchase-row-${p.id}`}>
                    <td className="px-3 py-3">
                      <button onClick={() => {
                        const next = expanded === p.id ? null : p.id;
                        setExpanded(next);
                        if (next && !payHistory[p.id]) {
                          api.get(`/admin/purchases/${p.id}/payments`)
                            .then(({ data }) => setPayHistory((h) => ({ ...h, [p.id]: data.payments })))
                            .catch(() => {});
                        }
                      }} aria-label="Items"
                        data-testid={`purchase-expand-${p.id}`} className="text-neutral-500 hover:text-amber-300">
                        {expanded === p.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs font-bold text-amber-400">{p.purchase_number}</td>
                    <td className="px-4 py-3 text-neutral-300">{p.supplier_name}</td>
                    <td className="px-4 py-3 text-xs text-neutral-500">{p.purchase_date}</td>
                    <td className="px-4 py-3 font-bold text-white">{fmt(p.total)}</td>
                    <td className="px-4 py-3 text-emerald-400">{fmt(p.amount_paid ?? 0)}</td>
                    <td className={`px-4 py-3 font-bold ${(p.outstanding ?? p.total) > 0 ? "text-amber-300" : "text-neutral-500"}`} data-testid={`purchase-outstanding-${p.id}`}>
                      {fmt(p.outstanding ?? p.total)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${p.status === "received" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}
                        data-testid={`purchase-status-${p.id}`}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="flex items-center justify-end gap-2">
                        {p.status === "ordered" && (
                          <button onClick={() => receive(p)} data-testid={`purchase-receive-${p.id}`}
                            className="flex h-8 items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/20">
                            <PackageCheck className="h-3.5 w-3.5" /> Receive
                          </button>
                        )}
                        {(p.outstanding ?? p.total) > 0 && (
                          <button onClick={() => openPay(p)} data-testid={`purchase-pay-${p.id}`}
                            className="flex h-8 items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 text-[11px] font-bold text-amber-300 hover:bg-amber-500/20">
                            <IndianRupee className="h-3.5 w-3.5" /> Record payment
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                  {expanded === p.id && (
                    <tr className="border-b border-white/[0.05] bg-black/30" data-testid={`purchase-items-${p.id}`}>
                      <td colSpan={9} className="px-6 py-4">
                        <div className="space-y-1.5">
                          {p.items.map((i, idx) => (
                            <p key={idx} className="text-xs text-neutral-400">
                              {i.product_name} ({i.sku}) — {i.qty} pcs @ {fmt(i.unit_cost)} · GST {i.gst_rate}% · {fmt(i.qty * i.unit_cost)}
                            </p>
                          ))}
                          <p className="pt-1 text-[11px] text-neutral-600">
                            Subtotal {fmt(p.subtotal)} + GST {fmt(p.tax)} = {fmt(p.total)}
                            {p.reference_invoice && ` · Ref ${p.reference_invoice}`}
                            {p.received_at && ` · Received ${p.received_at.slice(0, 10)}`}
                          </p>
                          {(payHistory[p.id] || []).length > 0 && (
                            <div className="mt-2 border-t border-white/[0.07] pt-2" data-testid={`purchase-payments-${p.id}`}>
                              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Payment history</p>
                              {payHistory[p.id].map((pay) => (
                                <p key={pay.id} className="text-xs text-neutral-400">
                                  {fmt(pay.amount)} · {pay.payment_date} · {pay.method.replace(/_/g, " ")}
                                  {pay.reference && ` · ref ${pay.reference}`} · by {pay.created_by}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paying && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="payment-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#F1EDE3] p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="font-mono text-lg font-bold text-amber-400">{paying.purchase_number}</h3>
                <p className="text-xs text-neutral-500">{paying.supplier_name} · total {fmt(paying.total)} · outstanding {fmt(paying.outstanding ?? paying.total)}</p>
              </div>
              <button onClick={() => setPaying(null)} aria-label="Close" data-testid="payment-modal-close"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Amount ₹</label>
                  <input type="number" min="0.01" step="0.01" value={payForm.amount} data-testid="payment-amount"
                    onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} className={input} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Payment date</label>
                  <input type="date" value={payForm.payment_date} data-testid="payment-date"
                    onChange={(e) => setPayForm((f) => ({ ...f, payment_date: e.target.value }))} className={input} />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Method</label>
                <select value={payForm.method} data-testid="payment-method"
                  onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))} className={input}>
                  {PAY_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <input value={payForm.reference} placeholder="Reference — UTR / cheque no. (optional)" data-testid="payment-reference"
                onChange={(e) => setPayForm((f) => ({ ...f, reference: e.target.value }))} className={input} />
              <input value={payForm.note} placeholder="Note (optional)" data-testid="payment-note"
                onChange={(e) => setPayForm((f) => ({ ...f, note: e.target.value }))} className={input} />
              <button onClick={submitPayment} disabled={payBusy} data-testid="payment-submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
                {payBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                Record payment{payForm.amount ? ` · ${fmt(parseFloat(payForm.amount) || 0)}` : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
